import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';

export type ToastTone = 'info' | 'success' | 'danger';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface Toast {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
}

interface ToastOptions {
  tone?: ToastTone;
  action?: ToastAction;
  /** Mặc định 4 giây; thông báo có nút Hoàn tác nên để 5 giây cho kịp bấm. */
  durationMs?: number;
}

interface ToastContextValue {
  showToast: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_CLASS: Record<ToastTone, string> = {
  info: 'toast-item',
  success: 'toast-item toast-success',
  danger: 'toast-item toast-danger'
};

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts(current => current.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, options?: ToastOptions) => {
      const id = nextId.current++;
      const toast: Toast = { id, message, tone: options?.tone ?? 'info', action: options?.action };

      setToasts(current => [...current, toast]);

      window.setTimeout(() => dismiss(id), options?.durationMs ?? 4000);
    },
    [dismiss]
  );

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

function ToastStack({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) {
  const { t } = useI18n();

  return (
    <div className="toast-stack" aria-live="polite" aria-atomic="true">
      {toasts.map(toast => (
        <div key={toast.id} className={TONE_CLASS[toast.tone]} role="status" data-testid="toast">
          <span className="toast-text">{toast.message}</span>

          {toast.action && (
            <button
              type="button"
              className="toast-action"
              data-testid="toast-action"
              onClick={() => {
                onDismiss(toast.id);
                toast.action!.onClick();
              }}
            >
              {toast.action.label}
            </button>
          )}

          <button
            type="button"
            className="toast-close"
            aria-label={t('common.dismiss')}
            onClick={() => onDismiss(toast.id)}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);

  if (!context) {
    throw new Error('useToast phải được dùng bên trong ToastProvider.');
  }

  return context;
}
